import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { notifyPdfNoStamp } from "@/lib/printNotice";
import { Printer, FileText, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { printZpl, setSelectedDevice, clearSelectedDevice } from "@/lib/zebra";
import { usePrinterTarget } from "@/lib/usePrinterTarget";
import { printerDisplayName } from "@/lib/printerMap";
import { PrintDestination } from "@/components/PrintDestination";

// Single location label = Option A (code-forward, 4x3 landscape, 300 DPI, QR +
// Code128, no branding). Reached from any "Label" button (/label/:id) or the
// Shelf Labels screen — both render the same thermal template.
export default function LabelPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);     // { code, resolved, zpl }
  const [pdfUrl, setPdfUrl] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const printer = usePrinterTarget("sticker");   // location labels are 4x3 stock

  useEffect(() => {
    let url = "";
    (async () => {
      try {
        const { data } = await api.get("/locations/label-zpl", { params: { value: id } });
        setInfo(data);
        const res = await api.get("/locations/label-pdf", { params: { value: id }, responseType: "blob" });
        url = URL.createObjectURL(res.data);
        setPdfUrl(url);
      } catch (e) {
        setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
      }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id]);

  const printZ = async () => {
    if (!info?.zpl || busy) return;
    if (!printer.target) { toast.error(printer.needsSetup ? "No printer is set for Sticker (4×3) stock. Open Printer Setup and map it — nothing was printed." : "No printer detected — start Zebra Browser Print."); return; }
    setBusy(true);
    setSelectedDevice(printer.target);
    try {
      const dev = await printZpl(info.zpl, null, "sticker");   // 4x3 sticker stock only
      // confirm-driven stamp: only when it resolves to a real saved location
      if (info.id) await api.post("/locations/label-printed/confirm", { location_ids: [info.id] }).catch(() => {});
      toast.success(`Sent ${info.code} to ${printerDisplayName(dev)}.`);
    } catch (e) {
      const msg = String(e?.message || "").toLowerCase().includes("fetch")
        ? "Zebra Browser Print not reachable — use the PDF, or check the printer connection."
        : formatApiErrorDetail(e.response?.data?.detail || e.message);
      toast.error(msg);
    } finally { clearSelectedDevice(); setBusy(false); }
  };

  return (
    <div data-testid="label-page" className="max-w-2xl space-y-5">
      <button data-testid="label-back" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div>
        <h1 className="font-display text-2xl font-black tracking-tight">Location Label</h1>
        <p className="text-sm text-slate-500 font-mono">{info?.code || "…"} · 4×3 landscape · 300 DPI</p>
      </div>

      {err && <div className="text-sm text-[#B01E28] font-mono">{err}</div>}

      <PrintDestination {...printer} testid="label-destination" />

      <div className="flex flex-wrap gap-2">
        <Button data-testid="label-print-zpl" onClick={printZ} disabled={!info?.zpl || busy} className="rounded-lg h-11 bg-[#1F1F1F] text-white"><Printer className="w-4 h-4 mr-1.5" />Print (ZPL)</Button>
        <Button data-testid="label-pdf" onClick={() => { if (pdfUrl) { window.open(pdfUrl, "_blank"); notifyPdfNoStamp(); } }} disabled={!pdfUrl} variant="outline" className="rounded-lg h-11"><FileText className="w-4 h-4 mr-1.5" />Open PDF</Button>
      </div>

      <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
        {pdfUrl
          ? <iframe data-testid="label-preview" title="label preview" src={pdfUrl} className="w-full" style={{ height: "360px", border: 0 }} />
          : <div className="h-[360px] flex items-center justify-center text-sm text-slate-400 font-mono">Rendering preview…</div>}
      </div>
    </div>
  );
}
